import React, { useEffect, useState } from 'react';
import {
  Button,
  colors,
  Select,
  Table,
  TableBody,
  TableHeader,
  TableHeading,
  TableRow,
} from '@insurancemenu/imenu-components-lib';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import clsx from 'clsx';
import { CloudUpload, FileEdi, Share } from '@insurancemenu/imenu-components-lib/dist/icons';
import ActionModal from 'components/common/modals/ActionModal';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { FileExchangeService } from 'types/exchangeService';
import PdfTableRow from './EdiTableRow';
import { createStyles } from './styles';
import UploadFileModal from '../UploadFileModal';
import GenerateFileModal from './GenerateFileModal';

const ReadyToSendEdis = observer((): JSX.Element => {
  const classes = createStyles();
  const {
    readyToSendEdisStore: {
      loading,
      exchangeReadyFiles,
      isModalOpen,
      selectedFile,
      modalType,
      getExchangeReadyFiles,
      closeModal,
      sendFile,
      openModal,
    },
    filesTrackingController: { openUploadModal, showToast, getSponsorOptions, setIsFileUploadReset, isUploadModalOpen },
  } = useRootStore();

  const [sponsorId, setSponsorId] = useState('122'); // NEDD - default sponsor
  const convertedFiles = exchangeReadyFiles.map((exchangeReadyFile) => toJS(exchangeReadyFile)).slice();
  const convertedSelectedFile = selectedFile ? toJS(selectedFile) : null;

  useEffect(() => {
    void getExchangeReadyFiles(sponsorId);
  }, [sponsorId]);

  const sendCurrentFile = async (): Promise<void> => {
    if (convertedSelectedFile?.id) {
      await sendFile(convertedSelectedFile?.id, sponsorId);
      void getExchangeReadyFiles(sponsorId);
    } else {
      showToast(
        'An issue has occurred while attempting to resend the file. Please try again later.',
        ToastColorTypes.WARNING,
      );
    }
    closeModal();
  };

  const EdiModal = (): JSX.Element => {
    switch (modalType) {
      case 'SEND_FILE':
        return (
          <ActionModal
            show={isModalOpen}
            title="Send File"
            subtitle={
              <span>
                You are about to send
                <br />
                <b className={classes.selectedFile}>${convertedSelectedFile?.fileLocation || 'this'}</b> file.
              </span>
            }
            onClose={closeModal}
            actionButtonText="Send File"
            onAction={sendCurrentFile}
            icon={<Share width="4.8rem" color={colors.green[600]} />}
          />
        );
      case 'GENERATE_FILE':
        return <GenerateFileModal defaultSponsorId={sponsorId} isModalOpen={isModalOpen} />;
      default:
        return <></>;
    }
  };
  const filesList = convertedFiles.map((file) => <PdfTableRow key={file.id} file={file} />);

  return (
    <div>
      <div className={classes.actionBar}>
        <Select
          label="Sponsor Id"
          name="sponsorId"
          options={getSponsorOptions()}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
            setSponsorId(e.target.value);
          }}
          value={sponsorId}
          width="25rem"
          validationPassed={!!sponsorId}
          validationMessage="Please select sponsor"
        />
        <div className={classes.buttonsBar}>
          <Button
            onClick={(): void => {
              setIsFileUploadReset(false);
              openUploadModal();
            }}
            size="large"
            color="primary"
            leftIconProp={<CloudUpload />}
            className={classes.uploadButton}
          >
            Upload File
          </Button>
          <Button
            onClick={(): void => {
              openModal('GENERATE_FILE');
            }}
            size="large"
            color="primary"
            leftIconProp={<FileEdi />}
            className={classes.uploadButton}
          >
            Generate EDI
          </Button>
        </div>
      </div>
      <p className={classes.infoTitle}>
        Total Files:<b className={classes.totalAmount}>{convertedFiles.length}</b>
      </p>
      <div className={classes.tableWrapper}>
        <Table
          className={classes.table}
          loading={loading}
          maxHeight="55rem"
          minHeight="55rem"
          totalElements={convertedFiles.length}
        >
          <TableHeader className={classes.tableHeader}>
            <TableRow>
              <TableHeading className={clsx(classes.tableHeading, classes.longFileInfo)}>File Id</TableHeading>
              <TableHeading className={clsx(classes.tableHeading, classes.longFileInfo)}>Policy Ids</TableHeading>
              <TableHeading className={clsx(classes.tableHeading, classes.longFileInfo)}>File Location</TableHeading>
              <TableHeading className={classes.tableHeading}>Creation Date</TableHeading>
              <TableHeading className={classes.tableHeading} />
              <TableHeading className={classes.tableHeading} />
            </TableRow>
          </TableHeader>
          <TableBody className={classes.tableBody}>{filesList}</TableBody>
        </Table>
      </div>
      {EdiModal()}
      <UploadFileModal
        allowMultipleFiles
        show={isUploadModalOpen}
        uploadFileFormat={FileExchangeService.EDI}
        sponsorId={sponsorId}
        onUpdate={(sponsor: string): void => {
          setSponsorId(sponsor);
          void getExchangeReadyFiles(sponsor);
        }}
      />
    </div>
  );
});

export default ReadyToSendEdis;
